import { generateUrl } from "v4/services/api.service";
import { FILES_VIEW } from "v4/data/apiRoutes";

class CidImageReplacer {
    constructor() {
        this.cidToBlobCache = new Map();
        this.blobToCidCache = new Map();
        this.pendingFetches = new Map();
        this.updateTimeout = null;
    }

    /**
     * Neutralizes cid: URLs by replacing them with empty strings and storing the cid in data-cid.
     * This prevents the browser from trying to fetch "cid:..." and getting errors.
     */
    prepare(html) {
        if (!html) return html;
        // Replace src="cid:xxx" with src="" data-cid="xxx" to prevent 404s/errors
        return html.replace(/src="cid:([^"]+)"/g, 'src="" data-cid="$1"');
    }

    /**
     * Replaces cid: or data-cid references with blob URLs from cache.
     * Triggers background fetches for missing ones.
     */
    sync(html, files = [], onUpdate = () => {}) {
        if (!html) return html;

        const tmpElement = document.createElement('div');
        tmpElement.innerHTML = html;
        const images = tmpElement.querySelectorAll('img');

        let hasCidImages = false;

        images.forEach(img => {
            const src = img.getAttribute('src') || '';
            const dataCid = img.getAttribute('data-cid');
            
            const cid = src.startsWith('cid:') ? src.replace('cid:', '') : dataCid;

            if (!cid) return;

            hasCidImages = true;

            const cachedBlob = this.cidToBlobCache.get(`cid:${cid}`);

            if (cachedBlob) {
                img.setAttribute('src', cachedBlob);
                img.removeAttribute('data-cid');
                return;
            }

            // Not in cache, ensure it's "prepared" (empty src)
            img.setAttribute('src', '');
            img.setAttribute('data-cid', cid);

            // Trigger fetch
            this._fetchCid(cid, files, onUpdate);
        });

        return hasCidImages ? tmpElement.innerHTML : html;
    }

    /**
     * Reverts blob URLs and data-cid markers back to cid: sources for submission.
     */
    revert(html) {
        if (!html) return html;
        
        let result = html;
        this.blobToCidCache.forEach((cidSrc, blobUrl) => {
            result = result.replaceAll(blobUrl, cidSrc);
        });

        if (result.includes('data-cid=')) {
            const tmpElement = document.createElement('div');
            tmpElement.innerHTML = result;
            tmpElement.querySelectorAll('img[data-cid]').forEach(img => {
                const cid = img.getAttribute('data-cid');
                img.setAttribute('src', `cid:${cid}`);
                img.removeAttribute('data-cid');
            });
            result = tmpElement.innerHTML;
        }
        
        return result;
    }

    _fetchCid(cid, files, onUpdate) {
        const cidSrc = `cid:${cid}`;
        if (this.pendingFetches.has(cidSrc)) return;

        const file = files.find(({ id, externalId }) => id === cid || externalId === cid);
        if (!file) return;

        const fetchPromise = (async () => {
            try {
                const response = await fetch(generateUrl(FILES_VIEW, { id: file.id }));

                if (!response.ok) throw new Error('Fetch failed');

                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);
                
                this.cidToBlobCache.set(cidSrc, blobUrl);
                this.blobToCidCache.set(blobUrl, cidSrc);
                
                this._notifyUpdate(onUpdate);
            } catch (e) {
                console.error(`CidImageReplacer: Failed to fetch ${cidSrc}`, e);
            } finally {
                this.pendingFetches.delete(cidSrc);
            }
        })();

        this.pendingFetches.set(cidSrc, fetchPromise);
    }

    /**
     * Debounces the update notification to avoid excessive re-renders
     * when multiple images are loaded simultaneously.
     */
    _notifyUpdate(onUpdate) {
        if (this.updateTimeout) clearTimeout(this.updateTimeout);

        this.updateTimeout = setTimeout(() => {
            onUpdate();
            this.updateTimeout = null;
        }, 100);
    }
}

// Export a singleton instance
export const cidImageReplacer = new CidImageReplacer();
